import React, { useCallback, useEffect, useState } from 'react';
import { View, TouchableOpacity, FlatList, ActivityIndicator } from 'react-native';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp, faFile, faPaperclip, IconDefinition } from '@fortawesome/free-solid-svg-icons';
import MyAppText from '../components/MyAppText';
import { translate } from '../services/translate';
import { NavigationProp, useFocusEffect, useIsFocused, useNavigation, useNavigationState } from '@react-navigation/native';
import UserInformationModal from '../components/UserInformationModal';
import { RootStackParamList } from '../typings/Params';
import { platformSettingsService } from '../services/central-api/platform-settings';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import { useAuth } from '../context/Auth';
import FilterInput from '../components/Filter/components/FilterInput';
import useDebounce from '../components/hooks/useDebounce';
import Container from '../components/Container';
import Header from '../components/Header';
import Toast from 'react-native-toast-message';
import { faArrowDownToSquare } from '@fortawesome/pro-regular-svg-icons';

export default function FrequentlyAskedQuestions() {
    const { styles, theme } = useStyles(styleSheet);
    const { user: authenticatedUser } = useAuth();

    const index = useNavigationState(state => state);
    const [showDropDown, setShowDropDown] = useState<boolean>(false);
    const [frequentlyAskedQuestions, setFrequentlyAskedQuestions] = useState<FrequentlyAskedQuestion[]>([]);
    const [filteredFrequentlyAskedQuestions, setFilteredFrequentlyAskedQuestions] = useState<FrequentlyAskedQuestion[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const navigation = useNavigation<NavigationProp<RootStackParamList>>();
    const [expandedIds, setExpandedIds] = useState<number[]>([]);
    const [textFilter, setTextFilter] = useState('');
    const debouncedFilter = useDebounce(textFilter, 300);

    async function getFrequentlyAskedQuestions() {
        try {
            setIsLoading(true);
            const response = await platformSettingsService.getFrequentlyAskedQuestions();
            setFrequentlyAskedQuestions(response);
            setFilteredFrequentlyAskedQuestions(response);
        } catch (err) {
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    }

    function goBack() {
        if (index.routes.length > 1) {
            return navigation.goBack();
        }
        window.location.href = '/menu';
    }

    async function downloadFile(url: string, filename: string) {
        try {
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }
            const blob = await response.blob();
            const blobUrl = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(blobUrl);
        } catch (error) {
            console.error('Download failed:', error);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    function renderItem({ item }: { item: FrequentlyAskedQuestion; }) {
        const expanded = expandedIds.includes(item.id);

        function getLimitedText(text: string) {
            return text.length > 25 ? `${text.substring(0, 17)}...` : text;
        }

        return (
            <TouchableOpacity style={styles.faqCard} onPress={() => setExpandedIds(prev => prev.includes(item.id) ? prev.filter(x => x !== item.id) : [...prev, item.id])} activeOpacity={0.8}>
                <View style={styles.faqHeader}>
                    <MyAppText style={styles.questionText}>{item.question}</MyAppText>
                    <View style={{ flexDirection: 'row', gap: 8, alignItems: 'center' }}>
                        {item.fileUrl && item.fileName && <View style={{ flexDirection: 'row', gap: 2, alignItems: 'center' }}>
                            <FontAwesomeIcon icon={faPaperclip} fontSize={14} color={theme.colors.neutral['400']}/>
                            <MyAppText style={{ fontSize: 14, fontWeight: '600', color: theme.colors.neutral['400'] }}>1</MyAppText>
                        </View>
                        }
                        <FontAwesomeIcon icon={expanded ? faChevronUp : faChevronDown} fontSize={14} color={theme.colors.iconColor} />
                    </View>
                </View>
                {expanded && (
                    <View style={styles.answerContainer}>
                        <MyAppText style={styles.answerText}>{item.answer}</MyAppText>
                        {item.fileUrl && item.fileName && <TouchableOpacity style={styles.downloadFileContainer} onPress={() => downloadFile(item.fileUrl!, item.fileName!)}>
                            <View style={{ flexDirection: 'row', gap: 16, alignItems:'center' }}>
                                <FontAwesomeIcon icon={faFile} fontSize={32} color={theme.colors.white['500']} />
                                <MyAppText numberOfLines={1} style={{ fontSize: 14, fontWeight: '600', color: theme.colors.white['500'] }}>{getLimitedText(item.fileName)}</MyAppText>

                            </View>
                            <FontAwesomeIcon icon={faArrowDownToSquare as IconDefinition} fontSize={24} color={theme.colors.neutral['300']} />
                        </TouchableOpacity>
                        }
                    </View>
                )}
            </TouchableOpacity>
        );
    }

    useEffect(() => {
        const normalizedFilter = debouncedFilter.trim().toLowerCase();
        if (!normalizedFilter) {
            setFilteredFrequentlyAskedQuestions(frequentlyAskedQuestions);
            return;
        }
        setFilteredFrequentlyAskedQuestions(
            frequentlyAskedQuestions.filter(faq =>
                faq.question.toLowerCase().includes(normalizedFilter) ||
                faq.answer.toLowerCase().includes(normalizedFilter)
            )
        );
    }, [debouncedFilter, frequentlyAskedQuestions]);


    const isFocused = useIsFocused();
    useFocusEffect(useCallback(() => {
        if (isFocused) {
            getFrequentlyAskedQuestions();
        }
    }, [isFocused, authenticatedUser]));

    if (isLoading) {
        return (
            <View style={[styles.container, { justifyContent: 'center', alignItems: 'center' }]}>
                <ActivityIndicator size='large' color={theme.colors.iconColor} />
            </View>
        );
    }

    return (
        <Container>
            <Header
                title={translate('frequentlyAskedQuestions')}
                goBackAction={goBack}
                actions={<FilterInput
                    value={textFilter}
                    placeholder={translate('typeToSearch')}
                    onChange={setTextFilter}
                    showIcon={true}
                />}
            />
            <FlatList
                data={filteredFrequentlyAskedQuestions}
                keyExtractor={(item) => item.id.toString()}
                renderItem={renderItem}
                contentContainerStyle={{ rowGap: 12 }}
            />
            <UserInformationModal
                showDropDown={showDropDown}
                setShowDropDown={setShowDropDown}
                navigation={navigation}
            />
        </Container>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: {
        rowGap: 15,
        flex: 1,
        paddingHorizontal: 10
    },
    faqCard: {
        backgroundColor: theme.colors.container.background,
        borderRadius: 12,
        padding: 12,
        borderWidth: 1,
        borderColor: theme.colors.container.border,
        shadowColor: '#000',
        shadowOpacity: 0.05,
        shadowRadius: 4,
        elevation: 2,
    },
    faqHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    questionText: {
        fontWeight: '600',
        fontSize: 16,
        color: theme.colors.neutral['500'],
        flex: 1,
        marginRight: 10,
    },
    answerContainer: {
        marginTop: 8,
        flexDirection: 'column',
        gap: 16
    },
    answerText: {
        fontSize: 14,
        color: theme.colors.neutral['400'],
        lineHeight: 20,
    },
    downloadFileContainer: {
        padding: 16,
        maxWidth: 295,
        flexDirection: 'row',
        justifyContent: 'space-between',
        backgroundColor: theme.colors.neutral['500'],
        borderRadius: 8,
        alignItems:'center'
    }
}));
